import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box as RebassBox, Card as CardRebass, Flex as RebassFlex, Heading, Text } from 'rebass';
import styled, { css } from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { iconTypes, ProjectIcons } from '../FontIcons';

const LinkWithFilteredProps = ({ className, to, children, ...props }) => (
  <RouterLink className={className} to={to} {...{ props }}>
    {children}
  </RouterLink>
);

export const ResetStyles = styled(RebassBox)`
  box-sizing: border-box;

  h1 {
    font-size: 32px;
    line-height: 64px;
  }

  h2 {
    font-size: 24px;
    line-height: 48px;
  }

  h3 {
    font-size: 18.72px;
    line-height: 37.44px;
  }

  h4 {
    font-size: 16px;
    line-height: 32px;
  }

  h5 {
    font-size: 13.28px;
    line-height: 26.56px;
  }

  h6 {
    font-size: 12px;
    line-height: 24px;
  }

  p {
    font-size: 16px;
    line-height: 28px;
  }

  em {
    font-style: italic;
  }

  b,
  strong {
    font-weight: bold;
  }

  ul,
  ol {
    margin-top: 10px;
    margin-left: 50px;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }
`;

const DataInputStylesFocus = css`
  box-shadow: rgb(25, 119, 166) 0 0 8px 0;
  outline-offset: 0;
  border-radius: 6px;
  margin-top: 2px;
  border-width: 2px;
  border-style: solid;
  border-color: rgb(148, 188, 210);
  border-image: initial;
`;

const Breakpoints = {
  sm: '40em',
  md: '52em',
  lg: '64em',
  xl: '80em',
  xxl: '90em',
};

const DataInputStyles = css`
  margin-bottom: ${(props) => (props.mb ? props.mb : '15px')};
  outline: none;
  color: #555555;
  margin-top: 3px;
  font-size: 16px;
  padding: 0.2em 0.5em;
  border-width: 1px;
  border-style: solid;
  border-color: transparent;
  border-image: initial;
  background: rgb(255, 255, 255);
  border-radius: 5px;
  box-shadow: rgb(229, 229, 229) 0 0 5px 2px;
  box-sizing: border-box;
  width: 100%;
  min-height: 38px;

  ::placeholder {
    font-size: 14px;
  }

  & :focus {
    ${DataInputStylesFocus};
  }
`;

const StyledHeading = styled(Heading)`
  color: #16689a;
  font-weight: 100;

  ${(props) =>
    props.as &&
    props.as.toString().toLowerCase() === 'h1' &&
    css`
      font-size: 32px;
    `}
  ${(props) =>
    props.as &&
    props.as.toString().toLowerCase() === 'h2' &&
    css`
      font-size: 28px;
    `}
    ${(props) =>
    props.as &&
    props.as.toString().toLowerCase() === 'h3' &&
    css`
      font-size: 26px;
    `}
    ${(props) =>
    props.as &&
    props.as.toString().toLowerCase() === 'h4' &&
    css`
      font-size: 24px;
    `}
    ${(props) =>
    props.as &&
    props.as.toString().toLowerCase() === 'h5' &&
    css`
      font-size: 22px;
    `}
    ${(props) =>
    props.as &&
    props.as.toString().toLowerCase() === 'h6' &&
    css`
      font-size: 20px;
    `}
    ${(props) =>
    props.centered
      ? css`
          text-align: center;
        `
      : null}
    p {
    display: inline-block;
    vertical-align: middle;
  }
`;

const TitleH1 = (props) => <StyledHeading pt="30px" pb="40px" {...props} as="h1" />;

const TitleH2 = (props) => <StyledHeading pt="30px" pb="40px" {...props} as="h2" />;

const TitleH3 = (props) => <StyledHeading pt="5px" pb="15px" fontWeight={200} {...props} as="h3" />;

const TitleH4 = (props) => <StyledHeading pt="20px" pb="30px" {...props} as="h4" />;

const TitleH5 = (props) => <StyledHeading pt="10px" pb="20px" {...props} as="h5" />;

const TitleH6 = (props) => <StyledHeading pt="10px" pb="20px" {...props} as="h6" />;

const Card = ({ children, ...props }) => (
  <RebassBox flex="0 300px" {...props} m="10px">
    <CardRebass bg="#ffffff" $borderRadius={8} boxShadow="0 2px 16px rgba(0, 0, 0, 0.25)">
      {children}
    </CardRebass>
  </RebassBox>
);

const HeaderSubPageIntl = ({ values, children, ...intlProps }) => {
  const intl = useIntl();
  return (
    <TitleH3 as="h3" paddingBottom="10px">
      {intl.formatMessage(intlProps, values)}
      {children}
    </TitleH3>
  );
};

const TopHelpText = styled(Text).attrs({
  as: 'p',
})`
  text-align: left;
  margin-top: 10px;
  margin-bottom: 3px;
  font-size: 14px;
  color: #888888;
  font-weight: normal;
`;

const CodText = styled(Text).attrs({
  as: 'p',
})`
  margin-top: 10px;
  margin-bottom: 3px;
  line-height: 1.2;

  span.success {
    padding: 1px 4px 2px;
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    text-decoration: none;
    line-height: 1;
    white-space: nowrap;
    display: inline;
    position: relative;
    bottom: 1px;
    color: #fff;
    background-color: #5da423;
  }

  span.secondary {
    padding: 1px 4px 2px;
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    text-decoration: none;
    line-height: 1;
    white-space: nowrap;
    display: inline;
    position: relative;
    bottom: 1px;
    background-color: #d5d5d5;
  }
`;

const BottomHelpText = styled(Text).attrs({
  as: 'p',
})`
  text-align: right;
  margin-top: 3px;
  margin-bottom: 10px;
  font-size: smaller;
  min-height: 14px;
`;

const HeaderAlertEvents = styled(RebassFlex)`
  flex-direction: column;
  justify-content: center;
  position: relative;
  padding: ${(props) => (props.padding ? props.padding : '20px')};
  background: ${(props) => (props.bg ? props.bg : props.called ? '#eeeeee' : '#ffffff')};
  border-radius: 10px;
  line-height: 1.2;
  color: ${(props) => (props.bg ? '#ffffff' : '#444444')};

  h6 {
    font-weight: ${(props) => props.bg && 'bolder '};
    color: ${(props) => props.bg && '#ffffff '};
    padding: 0px;
  }

  h2 {
    font-weight: 500;
  }

  b {
    margin: 5px 0;
  }

  span {
    font-weight: 400;
    font-size: 0.9em;
  }

  .icon-close-box {
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 10px;
    cursor: pointer;
  }
`;

const ContentAlert = ({
  children,
  title,
  close,
  itemPauta,
  duration,
  meetingFinished,
  ...props
}) => {
  const [openBox, setOpenBox] = useState(true);
  const [showItems, setShowItems] = useState(false);

  if (itemPauta) {
    return (
      <HeaderAlertEvents width={1} {...props}>
        {!meetingFinished && (
          <RebassBox>
            <span
              style={{ transform: showItems && 'rotate(180deg)' }}
              className="icon-close-box"
              onClick={() => setShowItems(!showItems)}
            >
              <ProjectIcons type={iconTypes.ARROW_DOWN} color="#065385" />
            </span>
            <RebassBox mr={30}>{itemPauta(showItems)}</RebassBox>
          </RebassBox>
        )}
        {(showItems || meetingFinished) && children}
      </HeaderAlertEvents>
    );
  }

  return (
    <>
      {openBox && (
        <HeaderAlertEvents width={1} {...props}>
          {title ? <TitleH6 m="0 0 20px"> {title} </TitleH6> : null}
          {close && (
            <span onClick={() => setOpenBox(false)} className="icon-close-box">
              <ProjectIcons type={iconTypes.CANCEL} color="#888888" />
            </span>
          )}
          {children && children}
        </HeaderAlertEvents>
      )}
    </>
  );
};

const TextGeneral = ({ txt }) =>
  txt.map((item) =>
    item.indexOf('\n')
      ? item.split('\n').map((p, idx) => (
          <RebassBox key={idx} mb="5px">
            {p} <br />
          </RebassBox>
        ))
      : txt,
  );

TextGeneral.propTypes = {
  txt: PropTypes.array.isRequired,
};

const ExternalLink = ({ className, children, ...props }) => (
  <a className={className} {...props}>
    {children}
  </a>
);

const StyledExternalLink = styled(ExternalLink)`
  display: inline-block;
  padding: ${({ padding }) => padding && padding};
  cursor: pointer;

  &:focus {
    outline: none;
  }
`;

const StyledLink = styled(LinkWithFilteredProps)`
  display: inline-block;
  padding: ${({ padding }) => padding || '10px 0px'};
  font-size: ${({ button }) => button && 'initial'};
  cursor: pointer;
`;

export const MessageSpanLink = styled.span`
  display: inline-block;

  & ${StyledLink}, & ${StyledExternalLink} {
    padding: 0 5px;
    color: #1779ba;
  }
`;

export const UpperFirstLetter = (comp) => styled(comp)`
  & {
    display: inline-block;

    ::first-letter {
      text-transform: uppercase;
    }
  }

  & :first-child {
    display: inline-block;

    ::first-letter {
      text-transform: uppercase;
    }
  }
`;

const FormatMessageWithInnerLink = ({ className, ...props }) => (
  <MessageSpanLink className={className}>
    <FormattedMessage {...props} />
  </MessageSpanLink>
);

const TagNames = styled.span`
  display: inline-block;
  position: relative;
  margin: ${(props) => (props.noMargin ? '0px' : '5px 10px')};
  padding: 5px;
  font-size: 14px;
  font-weight: normal;
  border-radius: 5px;
  background: ${(props) => (props.tags ? '#ccc' : '')};

  ${(props) =>
    props.edit &&
    css`
      :hover {
        :before {
          content: 'x';
          position: absolute;
          top: 3px;
          left: -7px;
          font-size: 14px;
          font-weight: bolder;
          width: 15px;
          color: #bbb;
        }
      }
    `})
`;

const ParameterName = styled.b`
  display: inline-block;
  padding: 2px 5px;
  color: #ffffff;
  border-radius: 5px;
  background: #5da423;
`;

const colorMap = {
  BLUE: 'BLUE',
  GREEN: 'GREEN',
  GRAY: 'GRAY',
  RED: 'RED',
  ORANGE: 'ORANGE',
  MAGENTA: 'MAGENTA',
  PURPLE: 'PURPLE',
  YELLOW: 'YELLOW',
};

const predefColors = {
  BLUE: '#60aee4',
  GREEN: '#00b300',
  GRAY: '#888888',
  RED: '#b90d1b',
  ORANGE: '#ffad33',
  MAGENTA: '#e600e6',
  PURPLE: '#990099',
  YELLOW: '#cccc00',
};

const backgroundColorSelector = (color = '') => {
  switch (color.toString().toUpperCase()) {
    case colorMap.BLUE:
      return predefColors.BLUE;
    case colorMap.GREEN:
      return predefColors.GREEN;
    case colorMap.GRAY:
      return predefColors.GRAY;
    case colorMap.RED:
      return predefColors.RED;
    case colorMap.ORANGE:
      return predefColors.ORANGE;
    case colorMap.MAGENTA:
      return predefColors.MAGENTA;
    case colorMap.PURPLE:
      return predefColors.PURPLE;
    case colorMap.YELLOW:
      return predefColors.YELLOW;
    default:
      return '#BBBBBB';
  }
};

const ActionButton = styled(StyledLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2ba6cb;
  font-size: 16px;
  border-radius: 5px;
  width: 20px;
  height: 20px;
  padding: 0;
  cursor: pointer;
  opacity: 1;
`;

const ButtonActionIcons = styled(RebassFlex)`
  justify-content: center;
  align-items: center;
  margin: ${({ margin }) => margin || '2px auto'};
  border-radius: 5px;
  width: 20px;
  height: 20px;
  text-shadow: 0 1px 0 #fff;
  opacity: 0.8;
  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;
      box-shadow:
        0 1px 0 white inset,
        0 0 5px rgba(0, 0, 0, 0.1) inset,
        0 1px 1px rgba(0, 0, 0, 0.3);
      background: #e5e5e5;
      background-image: -webkit-gradient(linear, 0 0, 0 100%, from(#f3f3f3), to(#e5e5e5));
      border: 1px solid #d5d5d5;
      opacity: 1;
    `};

  span {
    display: flex;
    align-items: center;
  }

  ${({ iconStroke }) =>
    !iconStroke
      ? css`
          svg,
          path {
            fill: ${({ validation }) =>
              validation === 'noAccepted'
                ? '#a1a1a1'
                : validation === true
                  ? '#62c2a5'
                  : '#f08080'};
          }
        `
      : css`
          svg,
          path {
            stroke: ${({ validation }) =>
              validation === 'noAccepted'
                ? '#a1a1a1'
                : validation === true
                  ? '#62c2a5'
                  : '#f08080'};
          }
        `}
  i {
    margin-top: 3px;
  }
`;

const ButtonRowIconsCheck = styled(RebassFlex)`
  justify-content: center;
  align-items: center;
  margin: 2px auto;
  border-radius: 5px;
  width: 20px;
  height: 20px;
  background: #e5e5e5;
  background-image: -webkit-gradient(linear, 0 0, 0 100%, from(#f3f3f3), to(#e5e5e5));
  text-shadow: 0 1px 0 #fff;
  border: 1px solid #d5d5d5;
  box-shadow:
    0 1px 0 white inset,
    0 0 5px rgba(0, 0, 0, 0.1) inset,
    0 1px 1px rgba(0, 0, 0, 0.3);
  cursor: ${(props) => props.onClick && 'pointer'};

  svg,
  path {
    fill: ${(props) =>
      props.checked ? '#62c2a5' : props.checked === false ? '#f08080' : '#888888'};
  }
`;

const LineHistoryStyle = styled.div`
  display: flex;
  align-items: center;
  background: #eee;
  padding: 10px 5px;
  border-radius: 5px;
  line-height: 1.4;
  font-size: 14px;
`;

const GroupLineHistory = styled.div`
  ${LineHistoryStyle}:nth-child(odd) {
    background-color: #ffffff;
  }
`;

const LineHistory = ({ children }) => (
  <LineHistoryStyle>
    <RebassBox>
      <ProjectIcons type={iconTypes.MINUS_CIRCLE} color="#888888" />
    </RebassBox>
    <RebassBox ml="10px">{children}</RebassBox>
  </LineHistoryStyle>
);

const CountryFlag = styled.img`
  margin: 0 5px;
  width: 15px;
  height: 15px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const TagCalled = styled(RebassBox)`
  display: inline-block;
  padding: 3px 15px;
  text-align: center;
  font-weight: normal;
  color: #ffffff;
  background: ${(props) =>
    props.background === 'open'
      ? '#62c2a5'
      : props.background === 'changed'
        ? 'orange'
        : props.background
          ? props.background
          : '#cccccc'};
  border-radius: 5px;
`;

const TitleLinkTab = styled.span`
  display: ${(props) => props.noWrapText && 'block'};
  max-width: ${(props) => (props.width ? props.width : '250px')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ImageOccurrence = styled.img`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  border-radius: 10px;
  cursor: ${(props) => props.onClick && 'pointer'};
`;

const IconStatus = styled(RebassBox)`
  width: 15px;
  height: 15px;
  margin-right: 5px;
  border-radius: 50%;
  background: ${(props) => props.color};
`;

export const ErrorBox = styled(RebassBox)`
  border: 1px solid #c2182b;
  border-radius: 10px;
  padding: 10px;
  line-height: 1.5;
  color: #c2182b;
  background-color: rgba(255, 50, 50, 0.3);
`;

export const LoadMoreProject = styled(RebassFlex)`
  justify-content: center;
  width: max-content;
  padding: 10px;
  color: #777777;
  background: #ffffff;
  border-radius: 5px;
  margin-bottom: 30px;
  transition: all 0.3s;

  :hover {
    cursor: pointer;
    transform: scale(1.05);
  }

  i {
    color: #62c2a5;
  }
`;

export const PermissionsIcon = ({ active, onClick, disabled = true, noPermission = false }) => {
  if (active) {
    return (
      <ButtonActionIcons validation={active} onClick={onClick}>
        <ProjectIcons type={iconTypes.CHECK} />
      </ButtonActionIcons>
    );
  }
  if (noPermission) {
    return (
      <ButtonActionIcons validation={!disabled ? 'noAccepted' : active} onClick={onClick}>
        <ProjectIcons type={iconTypes.ALERT} />
      </ButtonActionIcons>
    );
  }
  return (
    <ButtonActionIcons validation={!disabled ? 'noAccepted' : active} onClick={onClick}>
      <ProjectIcons type={iconTypes.CANCEL} />
    </ButtonActionIcons>
  );
};

PermissionsIcon.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

export {
  backgroundColorSelector,
  BottomHelpText,
  RebassBox as Box,
  ContentAlert,
  CountryFlag,
  Breakpoints,
  ActionButton,
  ButtonActionIcons,
  ButtonRowIconsCheck,
  Card,
  CodText,
  colorMap,
  DataInputStyles,
  DataInputStylesFocus,
  RebassFlex as Flex,
  FormatMessageWithInnerLink,
  HeaderSubPageIntl,
  IconStatus,
  ImageOccurrence,
  GroupLineHistory,
  LineHistory,
  ParameterName,
  predefColors,
  TagCalled,
  TextGeneral,
  TopHelpText,
  TitleH1,
  TitleH2,
  TitleH3,
  TitleH4,
  TitleH5,
  TitleH6,
  StyledExternalLink,
  StyledLink,
  TagNames,
  TitleLinkTab,
};
